import * as tdsConstants from '../utils/constants'
import uuid from 'uuid'
import { removeOperators, insertOperators } from '@/graph_parts/operators'
import * as go from 'gojs'
import { emitterSingleton } from '@/graph_parts/emitter'
import { orderByOrdering } from '@/utils/ordering'

let myEmitter = emitterSingleton.instance

// this function is used to highlight a Group that the selection may be dropped into
function highlightGroup (e, grp, show) {
  if (!grp) return
  e.handled = true
  if (show) {
    // cannot depend on the grp.diagram.selection in the case of external drag-and-drops
    // instead depend on the DraggingTool.draggedParts or .copiedParts
    var tool = grp.diagram.toolManager.draggingTool
    var map = tool.draggedParts || tool.copiedParts // this is a Map
    // now we can check to see if the Group will accept membership of the dragged Parts
    if (grp.canAddMembers(map.toKeySet())) {
      grp.isHighlighted = true
      return
    }
  }
  grp.isHighlighted = false
}

function groupsBackgroundAlternateWalk (g, flag) {
  if (g instanceof go.Group) {
    g.diagram.model.setDataProperty(g.data, 'backgroundAlt', flag)
    g.memberParts.each(function (m) {
      groupsBackgroundAlternateWalk(m, !flag)
    })
  }
}

function makeGroupsBackgroundAlternate (diagram) {
  diagram.findTopLevelGroups().each(function (g) {
    groupsBackgroundAlternateWalk(g, false)
  })
}

function combineDroppedNodeWithNode (e, part) {
  const newGroupKey = uuid()
  const nodeData = {
    key: newGroupKey,
    ...tdsConstants.defaultGroupAttributes,
    childrenPrefixOperatorVisible: e.diagram.tdsLayoutInline
  }
  if (e.diagram.tdsLayoutInline) {
    nodeData.layout = 'horizontal'
  }
  e.diagram.model.addNodeData(nodeData)
  part.diagram.selection.iterator.each(x => {
    e.diagram.model.setDataProperty(x.data, 'group', newGroupKey)
  })
  e.diagram.model.setDataProperty(part.data, 'group', newGroupKey)
}

function addPartsToGroupAtPosition (diagram, documentPoint, partsToAdd, part) {
  let targetOrdering = 10
  if (part.memberParts.count > 0) {
    const mp = documentPoint
    let nearestCentre = null
    let mostLr = null
    const mpIterator = part.memberParts
    let selectionKeys = []
    partsToAdd.each(x => {
      selectionKeys.push(x.data.key)
    })
    while (mpIterator.next()) {
      const v = mpIterator.value
      if (selectionKeys.includes(v.data.key)) {
        continue
      }
      const vab = v.actualBounds
      const centre = v.position.copy().offset(vab.width / 2, vab.height / 2)
      if (nearestCentre === null || centre.distanceSquaredPoint(mp) < nearestCentre.distanceSquaredPoint(mp)) {
        nearestCentre = centre
        const dp = centre.directionPoint(mp)
        targetOrdering = (dp >= 270 || dp <= 90) ? v.data.ordering + 10 : v.data.ordering
      }
      if (mostLr === null || mostLr.x < centre.x || mostLr.y < centre.y) {
        mostLr = new go.Point(
          mostLr === null ? centre.x : Math.max(centre.x, mostLr.x),
          mostLr === null ? centre.y : Math.max(centre.y, mostLr.y)
        )
      }
    }
    if (mostLr && nearestCentre &&
        mostLr.distanceSquaredPoint(mp) < nearestCentre.distanceSquaredPoint(mp)) {
      targetOrdering = (mpIterator.count + 1) * 10
    }
    /* GoJS angle: Zero is along the positive X-axis; 90 is along the positive Y-axis.
     *               270
     *               |
     *        node   |   +10
     *   180 --------------------0
     *        node   |   +10
     *               |
     *              90
     * mp click position    directionPoint from nearest node to click    ordering
     * above left           180 to 270                                   node
     * above right          270 top 360                                  node+10
     * below left           90 to 180                                    node
     * below right          0 to 90                                      node+10
     */
    // Move ordering up for those that follow position of dropped items
    const placingCount = part.diagram.selection.count
    part.memberParts.each(x => {
      if (x.data.ordering >= targetOrdering) {
        diagram.model.setDataProperty(x.data, 'ordering', parseInt(x.data.ordering) + 10 * placingCount)
      }
    })
  }
  // Set ordering on dropped items
  let droppedItemIndex = 0
  partsToAdd.each(x => {
    diagram.model.setDataProperty(x.data, 'ordering', targetOrdering + 10 * droppedItemIndex++)
  })
  // Finally add the selected items to the target group
  partsToAdd.iterator.each(x => {
    diagram.model.setDataProperty(x.data, 'group', part.data.key)
  })
}

function addDroppedItemToTopLevel (e, part) {
  if (e.targetObject === null) {
    const x = e.diagram.selection.iterator
    const model = e.diagram.model
    while (x.next()) {
      const data = x.value.data
      model.setDataProperty(data, 'ordering', null)
    }
    e.diagram.commandHandler.addTopLevelParts(e.diagram.selection, true)
  }
}

// Upon a drop onto a Group, we try to add the selection as members of the Group.
// Upon a drop onto the background, or onto a top-level Node, make selection top-level.
// If this is OK, we're done otherwise we cancel the operation to rollback everything.
function finishDrop (e, part) {
  // If there is nothing selected then it's simply something has been moved
  if (e.diagram.selection._count <= 0) {
    return
  }
  if (part && !part.data.isGroup && !part.isTopLevel) {
    return // Handled by call to finishDrop for group (since group has handlesDragDropForMembers set)
  }

  const isPartDefined = !(typeof part === 'undefined' || part === null)
  const gToUpdate = []
  let dropTarget = part
  if (isPartDefined && !part.data.isGroup && part.containingGroup) {
    dropTarget = part.containingGroup
  }

  if (isPartDefined && part.containingGroup) {
    gToUpdate.push(part.containingGroup)
  }
  e.diagram.selection.iterator.each(x => {
    if (x && x.containingGroup) {
      gToUpdate.push(x.containingGroup)
    }
  })

  if (isPartDefined) { // If dropping on something other than canvas
    part.diagram.selection.each(e => {
      e.diagram.model.setDataProperty(e.data, '_count', null)
      e.diagram.model.setDataProperty(e.data, '_showingCount', null)
    })
    if (!dropTarget.data.isGroup) { // If dropping on node that is sitting on canvas then combine into a new group
      e.diagram.model.setDataProperty(dropTarget.data, '_count', null)
      e.diagram.model.setDataProperty(dropTarget.data, '_showingCount', null)
      combineDroppedNodeWithNode(e, dropTarget)
    } else { // Dropping onto existing group
      addPartsToGroupAtPosition(e.diagram, e.documentPoint, part.diagram.selection, dropTarget)
      gToUpdate.push(dropTarget)
    }
  } else { // Dropping on canvas i.e. top-level
    addDroppedItemToTopLevel(e, dropTarget)
  }

  if (gToUpdate.length > 0) {
    const parentsToAdd = new Set()
    for (const g of gToUpdate) {
      let x = g.containingGroup
      while (x) {
        parentsToAdd.add(x)
        x = x.containingGroup
      }
    }
    if (parentsToAdd.size > 0) {
      parentsToAdd.forEach(p => {
        if (!gToUpdate.includes(p)) {
          gToUpdate.push(p)
        }
      })
    }
    for (const g of gToUpdate) {
      removeOperators(g, e.diagram)
      if (e.diagram.tdsLayoutInline) {
        insertOperators(g, e.diagram, g.data.operator, g.part.data.key)
      }
      if (!g.isTopLevel || !hasAnyNodes(g)) {
        e.diagram.model.setDataProperty(g.data, '_count', null)
        e.diagram.model.setDataProperty(g.data, '_showingCount', null)
      }
      updateOrdering(g, e.diagram)
    }
  }
  /*
  if (isPartDefined) {
    const newbnds = e.diagram.computePartsBounds(e.diagram.selection)
      .addMargin((part.placeholder && part.placeholder.padding) ? part.placeholder.padding : 0)
    e.diagram.moveParts(e.diagram.selection, gloc.subtract(newbnds.position), false)
  }
  */
  makeGroupsBackgroundAlternate(e.diagram)

  myEmitter.emitter.bus.$emit('queryChanged')

  e.diagram.layoutDiagram(true) // A bit heavy duty but couldn't get the layout to invalidate sometimes when moving a node within a group
}

// Set ordering to go up from 10 in increments of 10 e.g. after a node has been removed from the group
function updateOrdering (group, diagram) {
  const i = group.memberParts
  if (i.count > 0) {
    const p = []
    while (i.next()) {
      const v = i.value
      p.push(v)
    }
    p.sort(orderByOrdering)
    let ordering = 1
    p.forEach(x => {
      diagram.model.setDataProperty(x.data, 'ordering', ordering++ * 10)
    })
  }
}

function adjustGroupContentsFromSelection (e) {
  const x = e.diagram.selection.iterator
  while (x.next()) {
    const g = x.value.containingGroup
    if (g) {
      adjustGroupContents(e.diagram, g, x.value)
    }
  }
}

function adjustGroupContents (diagram, group, itemToIgnore) {
  const gi = group.memberParts
  let first = null
  while (gi.next()) {
    const m = gi.value
    if (first === null || m.position.x < first.x || m.position.y > first.y) {
      if (itemToIgnore && m.data.key !== itemToIgnore.data.key) {
        first = m
      }
    }
    m.position = new go.Point(NaN, NaN)
  }
  if (first) {
    first.position = group.placeholder.getDocumentPoint(go.Spot.TopLeft).offset(group.placeholder.padding)
  }
  gi.reset()
  while (gi.next()) {
    const m = gi.value
    if (m.data.isGroup) {
      adjustGroupContents(diagram, m)
    }
  }
  if (diagram.tdsLayoutInline) {
    removeOperators(group, diagram)
    insertOperators(group, diagram, group.data.operator, group.part.data.key)
  }
  group.invalidateLayout()
}

function hasAnyNodes (group) {
  if (!group) {
    return false
  }
  let r = false
  if (group.data.isGroup) {
    group.memberParts.each(x => {
      if (hasAnyNodes(x)) {
        return true
      }
    })
  }
  return r
}

export {
  highlightGroup,
  finishDrop,
  adjustGroupContentsFromSelection,
  adjustGroupContents,
  makeGroupsBackgroundAlternate,
  addPartsToGroupAtPosition
}



// WEBPACK FOOTER //
// ./src/graph_parts/moving.js